/**
 * Role Constants
 * User role definitions for the Uhamiaji SACCOS Management System
 */

export const ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  ACCOUNTANT: 'accountant',
  LOAN_OFFICER: 'loan_officer',
  TELLER: 'teller',
  MEMBER: 'member',
  AUDITOR: 'auditor',
};

/**
 * Role display names
 */
export const ROLE_DISPLAY_NAMES = {
  [ROLES.ADMIN]: 'System Administrator',
  [ROLES.MANAGER]: 'Branch Manager',
  [ROLES.ACCOUNTANT]: 'Accountant',
  [ROLES.LOAN_OFFICER]: 'Loan Officer',
  [ROLES.TELLER]: 'Teller',
  [ROLES.MEMBER]: 'Member',
  [ROLES.AUDITOR]: 'Auditor',
};

/**
 * Role descriptions
 */
export const ROLE_DESCRIPTIONS = {
  [ROLES.ADMIN]: 'Full system access. Can manage users, settings, and all modules.',
  [ROLES.MANAGER]: 'Oversees branch operations. Can approve loans and view all reports.',
  [ROLES.ACCOUNTANT]: 'Manages financial transactions, accounting entries, and financial reports.',
  [ROLES.LOAN_OFFICER]: 'Processes loan applications, manages guarantors, and tracks repayments.',
  [ROLES.TELLER]: 'Handles deposits, withdrawals, and daily cash transactions.',
  [ROLES.MEMBER]: 'Can view personal profile, savings, loans, and shares information.',
  [ROLES.AUDITOR]: 'Read-only access to all data for auditing purposes.',
};

/**
 * Role hierarchy (for permission inheritance)
 */
export const ROLE_HIERARCHY = {
  [ROLES.ADMIN]: 7,
  [ROLES.MANAGER]: 6,
  [ROLES.AUDITOR]: 5,
  [ROLES.ACCOUNTANT]: 4,
  [ROLES.LOAN_OFFICER]: 3,
  [ROLES.TELLER]: 2,
  [ROLES.MEMBER]: 1,
};

/**
 * Get role display name
 * @param {string} role - Role key
 * @returns {string} Display name
 */
export const getRoleDisplayName = (role) => {
  return ROLE_DISPLAY_NAMES[role] || role;
};

/**
 * Get role description
 * @param {string} role - Role key
 * @returns {string} Description
 */
export const getRoleDescription = (role) => {
  return ROLE_DESCRIPTIONS[role] || '';
};

/**
 * Check if role A has higher or equal authority than role B
 * @param {string} roleA - First role
 * @param {string} roleB - Second role
 * @returns {boolean}
 */
export const hasHigherOrEqualAuthority = (roleA, roleB) => {
  return ROLE_HIERARCHY[roleA] >= ROLE_HIERARCHY[roleB];
};

/**
 * Get all available roles as array of options
 * @returns {Array<{value: string, label: string}>}
 */
export const getRoleOptions = () => {
  return Object.values(ROLES).map((role) => ({
    value: role,
    label: ROLE_DISPLAY_NAMES[role],
  }));
};

export default ROLES;
